import React from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import { listSupportTeamApi, type SupportMember } from '@/api/admin';

/**
 * Who to message, shown to an operator from the profile menu.
 *
 * It used to open one personal Facebook page in a new tab, whoever happened to
 * be on duty. Now it shows the people actually answering, with what each of
 * them handles, so an operator with a billing question does not message the
 * person who fixes NodeMCUes.
 */

const HELP_URL = 'https://smartfi.ph/help';
/** Where an operator goes if nobody has been listed yet. */
const FALLBACK_URL = 'https://www.facebook.com/rayjensv2.0/';

const SupportTeam: React.FC = () => {
   const { data, isLoading } = useQuery({
      queryKey: ['support-team'],
      queryFn: listSupportTeamApi,
      staleTime: 5 * 60 * 1000,
   });

   const members = data?.members ?? [];

   return (
      <Body>
         <Lead>
            Message anyone below on Facebook. Tell them your device ID and what you were
            doing — that is usually everything they need to answer on the first reply.
         </Lead>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && !members.length && (
            <Note>
               <b>Nobody is listed yet.</b>
               <Link href={FALLBACK_URL} target="_blank" rel="noreferrer noopener">
                  Message SmartFi support →
               </Link>
            </Note>
         )}

         {members.map((m: SupportMember) => (
            <Person
               key={m._id}
               href={m.facebookUrl}
               target="_blank"
               rel="noreferrer noopener"
            >
               <Avatar $url={m.avatarUrl}>
                  {!m.avatarUrl && (m.name?.charAt(0).toUpperCase() || '?')}
               </Avatar>
               <Who>
                  <Name>{m.name}</Name>
                  {m.role && <Role>{m.role}</Role>}
               </Who>
               <Fb>
                  <IconsLib icon="mail-line" size="16px" color="#1877F2" />
               </Fb>
            </Person>
         ))}

         <Foot>
            Looking for a quick answer?{' '}
            <Link href={HELP_URL} target="_blank" rel="noreferrer noopener">
               Read the help centre →
            </Link>
         </Foot>
      </Body>
   );
};

export default SupportTeam;

/* ─── Styles ────────────────────────────────────────────────────────── */

const Body = styled.div`
   width: min(420px, 76vw);
   padding-top: 4px;

   @media (max-width: 768px) {
      width: 100%;
      padding-bottom: 1rem;
   }
`;

const Lead = styled.p`
   margin: 0 0 14px;
   font-size: 13px;
   line-height: 1.55;
   color: ${colors.GRAY3};
`;

const Person = styled.a`
   display: flex;
   align-items: center;
   gap: 11px;
   padding: 10px;
   border: 1px solid ${colors.GRAY11};
   border-radius: 8px;
   margin-bottom: 8px;
   text-decoration: none;

   &:hover {
      border-color: ${colors.PRIMARY};
      background: ${colors.GRAY14};
   }
`;

const Avatar = styled.span<{ $url?: string }>`
   width: 40px;
   height: 40px;
   border-radius: 50%;
   flex: none;
   display: grid;
   place-items: center;
   font-size: 15px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${({ $url }) =>
      $url ? `url(${$url}) center/cover no-repeat` : colors.PRIMARY};
`;

const Who = styled.span`
   flex: 1;
   min-width: 0;
`;

const Name = styled.span`
   display: block;
   font-size: 14px;
   font-weight: 650;
   color: ${colors.GRAY_DARK};
`;

const Role = styled.span`
   display: block;
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const Fb = styled.span`
   width: 30px;
   height: 30px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   background: rgba(24, 119, 242, 0.12);
   flex: none;
`;

const Note = styled.div`
   padding: 22px 12px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY3};

   b {
      display: block;
      color: ${colors.GRAY_DARK};
      font-size: 14.5px;
      margin-bottom: 6px;
   }
`;

const Foot = styled.p`
   margin: 14px 0 0;
   font-size: 12.5px;
   color: ${colors.GRAY3};
`;

const Link = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 650;
   text-decoration: none;
`;
